import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Trophy, ChevronDown, Layers, MapPin, RotateCw } from "lucide-react";
import useDocumentTitle from "../lib/useDocumentTitle";

const RECENT_CLIENTS = [
  { name: "Mr. DMS", img: "https://images.unsplash.com/photo-1742415106102-77bbfe14b872?w=200&h=200&fit=crop" },
  { name: "Mrs. F", img: "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?w=200&h=200&fit=crop" },
  { name: "Mr. Srinivas", img: "https://images.unsplash.com/photo-1668642538782-9579e75b0f4b?w=200&h=200&fit=crop" },
  { name: "Mr. Sahil", img: "https://images.unsplash.com/photo-1503387837-b154d5074bd2?w=200&h=200&fit=crop" },
  { name: "Mr. A.P. Singh", img: "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?w=200&h=200&fit=crop&q=80" },
  { name: "Mr. Ajay Jain", img: "https://images.unsplash.com/photo-1668642538782-9579e75b0f4b?w=200&h=200&fit=crop&q=85" },
  { name: "Mr. Bittu", img: "https://images.unsplash.com/photo-1627141234469-24711efb373c?w=200&h=200&fit=crop" },
  { name: "Mr. Himanshu", img: "https://images.pexels.com/photos/5563473/pexels-photo-5563473.jpeg?w=200&h=200&fit=crop" },
  { name: "Dr. Suresh", img: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=200&h=200&fit=crop" },
  { name: "Mrs. Meghna", img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&h=200&fit=crop" },
  { name: "Mr. Aditya", img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=200&fit=crop" },
  { name: "Mrs. Shalini", img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=200&h=200&fit=crop" },
];

const CATEGORIES = [
  { id: "all",          label: "All Portfolio" },
  { id: "house",        label: "House Design" },
  { id: "villa",        label: "Villa Design" },
  { id: "other",        label: "Other Building Design" },
  { id: "construction", label: "Construction Results" },
];

const AWARD_FILTERS = [
  { id: "all",    label: "All The Best Portfolio" },
  { id: "house",  label: "Best House Design" },
  { id: "villa",  label: "Best Villa Design" },
  { id: "other",  label: "Best Other Building Design" },
];

const STYLES = ["all", "classic", "modern", "tropical", "contemporary", "rajputana", "luxury", "italian", "colonial", "traditional"];
const FLOORS = ["all", "1", "2", "3", "4"];
const SORT_OPTIONS = [
  { id: "newest", label: "Sort by Newest" },
  { id: "oldest", label: "Sort by Oldest" },
  { id: "name",   label: "Sort by Name" },
];

export default function Portfolio() {
  useDocumentTitle("Portfolio", "500+ real house designs delivered by HouseDesignWala™ — browse 2D floor plans and 3D elevations across villas and houses, pan-India.");
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [award, setAward] = useState(null);
  const [style, setStyle] = useState("all");
  const [floor, setFloor] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => { api.get("/portfolio").then(r => setItems(r.data)); }, []);

  const filtered = useMemo(() => {
    let arr = items;
    if (category !== "all") arr = arr.filter(i => i.category === category);
    if (award) arr = arr.filter(i => i.achievement === "best" && (award === "all" || i.category === award));
    if (style !== "all") arr = arr.filter(i => i.style === style);
    if (floor !== "all") arr = arr.filter(i => i.floors === floor);
    if (sort === "name") arr = [...arr].sort((a, b) => a.client.localeCompare(b.client));
    return arr;
  }, [items, category, award, style, floor, sort]);

  return (
    <div className="bg-[#f8f8f8] min-h-screen">
      {/* Hero strip */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a3c5e] tracking-tight">All Portfolio</h1>
              <p className="text-sm text-gray-500 mt-1">Real homes designed and delivered across India</p>
            </div>
            <div className="text-xs text-[#F7941D] font-bold uppercase tracking-wider">
              {items.length} Projects · 28 States
            </div>
          </div>
        </div>
      </section>

      {/* Recent clients avatar strip */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex gap-5 overflow-x-auto scrollbar-thin pb-2" data-testid="recent-clients-strip">
            {RECENT_CLIENTS.map((c, i) => (
              <div key={i} className="flex flex-col items-center flex-shrink-0 group cursor-pointer">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full p-[2px] bg-gradient-to-br from-[#F7941D] via-[#D32F2F] to-[#F7941D] group-hover:scale-105 transition">
                  <div className="w-full h-full rounded-full overflow-hidden bg-white p-0.5">
                    <img src={c.img} alt={c.name} className="w-full h-full object-cover rounded-full" loading="lazy" />
                  </div>
                </div>
                <span className="text-[11px] text-[#1a3c5e] font-semibold mt-2 truncate max-w-[80px]">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-y border-gray-200 sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
          {/* Category pills */}
          <div className="flex flex-wrap gap-2" data-testid="portfolio-categories">
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                onClick={() => { setCategory(c.id); setAward(null); }}
                data-testid={`cat-${c.id}`}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition ${
                  category === c.id && !award ? "bg-[#1a3c5e] text-white" : "bg-gray-100 text-[#1a3c5e] hover:bg-gray-200"
                }`}
              >{c.label}</button>
            ))}
          </div>
          {/* Award pills */}
          <div className="flex flex-wrap gap-2" data-testid="portfolio-awards">
            {AWARD_FILTERS.map(a => (
              <button
                key={a.id}
                onClick={() => { setAward(award === a.id ? null : a.id); setCategory("all"); }}
                data-testid={`award-${a.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition ${
                  award === a.id ? "bg-[#F7941D] text-white" : "bg-[#FEF3E2]/70 text-[#F7941D] hover:bg-[#FEF3E2]"
                }`}
              ><Trophy className="w-3 h-3" /> {a.label}</button>
            ))}
          </div>
          {/* Dropdowns */}
          <div className="flex flex-wrap items-center gap-2">
            <Dropdown icon={<Layers className="w-3 h-3" />} value={style} onChange={setStyle} options={STYLES.map(s => ({ id: s, label: s === "all" ? "-- All Style --" : s[0].toUpperCase() + s.slice(1) }))} testId="style-filter" />
            <Dropdown icon={<MapPin className="w-3 h-3" />} value={floor} onChange={setFloor} options={FLOORS.map(f => ({ id: f, label: f === "all" ? "-- All Floors --" : `${f} Floor${f > 1 ? "s" : ""}` }))} testId="floor-filter" />
            <Dropdown value={sort} onChange={setSort} options={SORT_OPTIONS} testId="sort-filter" />
            <div className="ml-auto text-xs text-gray-500">
              <span className="font-semibold text-[#1a3c5e]">{filtered.length}</span> results
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio grid (Emporio-style: big hero + 2 thumbnails) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="portfolio-grid">
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500">No projects match your filters.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(p => <PortfolioCard key={p.id} p={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function PortfolioCard({ p }) {
  const t1 = p.thumbs?.[0] || p.image;
  const t2 = p.thumbs?.[1] || p.image;
  return (
    <Link to={`/portfolio/${p.id}`} data-testid={`portfolio-card-${p.id}`} className="group cursor-pointer block">
      {/* Main hero image */}
      <div className="aspect-[4/3] overflow-hidden rounded-md relative bg-gray-100">
        <img src={p.image} alt={p.client} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
        {p.achievement === "best" && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F7941D] text-white text-[10px] font-extrabold uppercase tracking-wider shadow-md">
            <Trophy className="w-3 h-3" /> Best Design
          </div>
        )}
        <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur rounded px-2 py-1 text-[10px] font-bold text-[#1a3c5e] uppercase">
          www.housedesignwala.com
        </div>
        {(p.media_360?.length || 0) > 0 && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-[10px] font-extrabold uppercase tracking-wider shadow-md">
            <RotateCw className="w-3 h-3" /> 360°
          </div>
        )}
      </div>
      {/* Two thumbnails */}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div className="aspect-[4/3] overflow-hidden rounded-md bg-gray-100">
          <img src={t1} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="aspect-[4/3] overflow-hidden rounded-md bg-gray-100">
          <img src={t2} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
      {/* Caption */}
      <div className="mt-3 pb-2">
        <h3 className="font-display font-bold text-[#1a3c5e] text-sm leading-tight group-hover:text-[#F7941D] transition" data-testid={`portfolio-title-${p.id}`}>
          {p.client} {p.style ? `${p.style[0].toUpperCase()}${p.style.slice(1)}` : ""} {p.service} {p.floors} Floor{p.floors > 1 ? "s" : ""} Design
        </h3>
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
          <span>{p.city}, {p.state}</span>
          {p.achievement === "best" && <span className="inline-flex items-center gap-0.5 text-[#F7941D] font-bold"><Trophy className="w-2.5 h-2.5" /> Award</span>}
        </div>
      </div>
    </Link>
  );
}

function Dropdown({ icon, value, onChange, options, testId }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="appearance-none border border-gray-200 rounded-md pl-3 pr-8 py-1.5 text-xs font-semibold bg-gray-50 text-[#1a3c5e] focus:outline-none focus:ring-2 focus:ring-[#F7941D] cursor-pointer"
      >
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#1a3c5e]/60" />
    </div>
  );
}
